/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect, useContext } from "react";
import { NavLink, useHistory, useParams } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import AllReports from "./AllReports";
import { STATES } from "../../constants/GeneralConstants";
import SearchMultiSelect from "../../components/SearchMultiSelect";
import { LayoutContext } from "../../components/layout";
import fetchHelper from "../../services/fetchHelper";
import { handleErrors } from "../../utils/helpers";
import produce from "immer";

const PatientsList = () => {
  const { user, currentLocation } = useContext(LayoutContext);
  const [selectedTab, setSelectedTab] = useState("patients");
  const [isLoading, setIsLoading] = useState(false);
  const [hasAdtIntegration, setHasAdtIntegration] = useState(false);
  const [mergePatients, setMergePatients] = useState({ from: null, to: null });
  const [patient, setPatient] = useState({ locations: [{ id: currentLocation.id }] });
  const [cancelRequest, setCancelRequest] = useState(new AbortController());
  const history = useHistory();
  const { tab } = useParams();
  const { addToast } = useToasts();

  useEffect(() => {
    if (typeof tab === "undefined") {
      setSelectedTab("patients");
      localStorage.setItem("selectedTab", "patients");
    } else setSelectedTab(tab);
  }, [tab]);

  useEffect(() => {
    setHasAdtIntegration(!!currentLocation.adt);
  }, [currentLocation]);

  const classNameTab = (pos) => {
    if (pos === selectedTab) return "tabActive";
    else return "";
  };

  const merge = async () => {
    try {
      setIsLoading(true);
      const url = "/patientmanagement/rest/patients/mergePatients";
      const data = { from: mergePatients.from.id, to: mergePatients.to.id };
      await fetchHelper.put(url, data);
      const link = document.createElement("a");
      link.href = "#";
      link.click();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      handleErrors(addToast, error);
    }
  };

  const onCreatePatient = async () => {
    try {
      setIsLoading(true);
      patient.pacemakerDep = null;
      const res = await fetchHelper.post("/patientmanagement/rest/patients", patient);
      setIsLoading(false);
      history.push(`/patients3/${res.id}`);
    } catch (error) {
      setIsLoading(false);
      handleErrors(addToast, error);
    }
  };

  const setPatientInfo = (e) => {
    const { name, value } = e.target;
    setPatient((prev) =>
      produce(prev, (draft) => {
        if (name === "locations") {
          draft.locations = [];
          draft.locations.push({ id: value });
        } else draft[name] = value;
      })
    );
  };

  const abortRequest = () => {
    cancelRequest.abort();
    setCancelRequest(new AbortController());
  };

  return (
    <div id="index" className="containerFlex maxHd">
      <div className="contentArea">
        <div className="up-interrog" style={{ display: "flex", justifyContent: "flex-end" }}>
          {tab === "patient-merge" ? (
            <a href="#mergePatients">
              <button
                id="merge-patient-btn"
                className="btn btnSuccess"
                disabled={!mergePatients?.from || !mergePatients?.to}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <div className="upload-icon icon-64" style={{ marginRight: "10px" }} />
                Merge Patient
              </button>
            </a>
          ) : (
            <a href="#addPatient">
              <button
                className="btn btnSuccess"
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <div className="upload-icon icon-64" style={{ marginRight: "10px" }} />
                Add Patient
              </button>
            </a>
          )}
        </div>

        <nav className="tabs" id="report-navigation">
          <NavLink
            to="/patients3"
            className={classNameTab("patients")}
            onClick={() => {
              abortRequest();
              setSelectedTab("patients");
              localStorage.setItem("selectedTab", "patients");
            }}
          >
            <span className="tab-text">Patients</span>
          </NavLink>
          <NavLink
            to="/patients3/insurance"
            className={classNameTab("insurance")}
            onClick={() => {
              abortRequest();
              setSelectedTab("insurance");
              localStorage.setItem("selectedTab", "insurance");
            }}
          >
            <span className="tab-text">Insurance</span>
          </NavLink>
          {hasAdtIntegration ? (
            <NavLink
              to="/patients3/pending"
              className={classNameTab("pending")}
              onClick={() => {
                abortRequest();
                setSelectedTab("pending");
                localStorage.setItem("selectedTab", "pending");
              }}
            >
              <span className="tab-text">Pending</span>
            </NavLink>
          ) : null}
          <NavLink
            to="/patients3/patient-merge"
            className={classNameTab("patient-merge")}
            onClick={() => {
              abortRequest();
              setSelectedTab("patient-merge");
              localStorage.setItem("selectedTab", "patient-merge");
            }}
          >
            <span id="patient-merge-tab" className="tab-text">
              Patient Merge
            </span>
          </NavLink>
        </nav>

        <div className="tabContent">
          <AllReports
            activeTab={selectedTab}
            mergePatients={mergePatients}
            onMergePatients={setMergePatients}
            cancelRequest={cancelRequest}
          />
        </div>
      </div>

      {/* Add patient Modal */}
      <div className="modal override-row create-interrogation-modal popup" id="addPatient">
        <div className="modalContent">
          <div className="grpHdr">
            <h3>Add Patient</h3>
          </div>
          <div className="row rowStretch">
            <div className="col col-s-100 col50">
              <label>
                <span>
                  Status<sub className="required-field">*</sub>
                </span>
                <select name="status" value={patient.status} onChange={setPatientInfo}>
                  <option value="">Select Status...</option>
                  {["ACTIVE", "DECEASED", "REMOVED"].map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="col col-s-100 col50">
              <label>
                <span>
                  State<sub className="required-field">*</sub>
                </span>
                <select name="patientState" value={patient.patientState} onChange={setPatientInfo}>
                  <option value="">Select State...</option>
                  {["ACTIVE", "LOCKED", "PENDING"].map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
          <div className="row rowStretch">
            <div className="col col-s-100 col30">
              <label>
                <span>
                  First Name<sub className="required-field">*</sub>
                </span>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter First Name..."
                  value={patient.name}
                  onChange={setPatientInfo}
                />
              </label>
            </div>
            <div className="col col-s-100 col30" style={{ marginLeft: 10, marginRight: 10 }}>
              <label>
                <span>Middle Name</span>
                <input
                  type="text"
                  name="middleName"
                  placeholder="Enter Middle Name..."
                  value={patient.middleName}
                  onChange={setPatientInfo}
                />
              </label>
            </div>
            <div className="col col-s-100 col30">
              <label>
                <span>
                  Last Name<sub className="required-field">*</sub>
                </span>
                <input
                  type="text"
                  name="family"
                  placeholder="Enter Last Name..."
                  value={patient.family}
                  onChange={setPatientInfo}
                />
              </label>
            </div>
          </div>
          <div className="row rowStretch">
            <div className="col col-s-100 col50">
              <div className="containerFlex containerFormLi" style={{ flexDirection: "column" }}>
                <label className="title-label">
                  <span>Gender</span>
                </label>
                <div style={{ marginTop: "20px" }}>
                  <label className="formLi flex0">
                    <input
                      type="radio"
                      name="gender"
                      className="chk"
                      value="MALE"
                      checked={patient.gender === "MALE"}
                      onChange={setPatientInfo}
                    />
                    <span className="chkmrk radio" />
                    <span className="chk-label">Male</span>
                  </label>
                  <label className="formLi flex0" style={{ marginLeft: "10px" }}>
                    <input
                      type="radio"
                      name="gender"
                      className="chk"
                      value="FEMALE"
                      checked={patient.gender === "FEMALE"}
                      onChange={setPatientInfo}
                    />
                    <span className="chkmrk radio" />
                    <span className="chk-label">Female</span>
                  </label>
                  <label className="formLi flex0" style={{ marginLeft: "10px" }}>
                    <input
                      type="radio"
                      name="gender"
                      className="chk"
                      value="UNKNOWN"
                      checked={patient.gender === "UNKNOWN"}
                      onChange={setPatientInfo}
                    />
                    <span className="chkmrk radio" />
                    <span className="chk-label">Unknown</span>
                  </label>
                  <label className="formLi flex0" style={{ marginTop: "0px" }}>
                    <input
                      type="radio"
                      name="gender"
                      className="chk"
                      value="NONBINARY"
                      checked={patient.gender === "NONBINARY"}
                      onChange={setPatientInfo}
                    />
                    <span className="chkmrk radio" />
                    <span className="chk-label">Nonbinary</span>
                  </label>
                  <label className="formLi flex0" style={{ marginTop: "0px", marginLeft: "10px" }}>
                    <input
                      type="radio"
                      name="gender"
                      className="chk"
                      value="OTHER"
                      checked={patient.gender === "OTHER"}
                      onChange={setPatientInfo}
                    />
                    <span className="chkmrk radio" />
                    <span className="chk-label">Other</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="col col-s-100 col50">
              <label className="inCal">
                <span>Date of Birth</span>
                <input type="date" name="dob" value={patient.dob} onChange={setPatientInfo} />
                <i className="ti ti-calendar" />
              </label>
            </div>
          </div>
          <div className="row rowStretch">
            <div className="col col-s-100 col50">
              <label>
                <span>Email</span>
                <input
                  type="text"
                  name="emailAddress"
                  value={patient.emailAddress}
                  onChange={setPatientInfo}
                />
              </label>
            </div>
            <div className="col col-s-100 col50">
              <label>
                <span>Home Phone</span>
                <input type="text" name="homePhone" value={patient.homePhone} onChange={setPatientInfo} />
              </label>
            </div>
          </div>
          <div className="row rowStretch">
            <div className="col col-s-100 col50">
              <label>
                <span>Mobile Phone</span>
                <input type="text" name="mobilePhone" value={patient.mobilePhone} onChange={setPatientInfo} />
              </label>
            </div>
            <div className="col col-s-100 col50">
              <label>
                <span>
                  MRN<sub className="required-field">*</sub>
                </span>
                <input type="text" name="mrn" value={patient.mrn} onChange={setPatientInfo} />
              </label>
            </div>
          </div>
          <div className="row rowStretch">
            <div className="col col-s-100 col50">
              <label>
                <span>Address Line 1</span>
                <input type="text" name="address1" value={patient.address1} onChange={setPatientInfo} />
              </label>
            </div>
            <div className="col col-s-100 col50">
              <label>
                <span>Address Line 2</span>
                <input type="text" name="address2" value={patient.address2} onChange={setPatientInfo} />
              </label>
            </div>
          </div>
          <div className="row rowStretch">
            <div className="col col-s-100 col10">
              <label>
                <span>City</span>
                <input type="text" name="city" value={patient.city} onChange={setPatientInfo} />
              </label>
            </div>
            <div className="col col-s-100 col10 form-dropdown" style={{ marginLeft: 10 }}>
              <label>
                <span>State</span>
                <SearchMultiSelect
                  options={Object.keys(STATES).map((state) => ({ label: STATES[state], value: state }))}
                  maxSelect={1}
                  hasPreview={false}
                  onChange={(value) => {
                    setPatient((prev) =>
                      produce(prev, (draft) => {
                        draft.state = value[0];
                      })
                    );
                  }}
                />
              </label>
            </div>
            <div className="col col-s-100 col10" style={{ marginLeft: 10, marginRight: 10 }}>
              <label>
                <span>Zip</span>
                <input type="text" name="zip" value={patient.zip} onChange={setPatientInfo} />
              </label>
            </div>
            <div className="col col-s-100 col10">
              <label>
                <span>Country</span>
                <input type="text" name="country" value={patient.country} onChange={setPatientInfo} />
              </label>
            </div>
          </div>
          <div className="row rowStretch">
            <div className="col col-s-100 col50">
              <label>
                <span>
                  Location<sub className="required-field">*</sub>
                </span>
                <select name="locations" value={patient.locations[0].id} onChange={setPatientInfo}>
                  <option value="">Select</option>
                  {user?.locations?.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="col col-s-100 col50" />
          </div>
          <div className="row rowStretch noMargin">
            <div className="col create-ir-col" style={{ display: "flex" }}>
              <button className="btn btnSuccess" disabled={isLoading} onClick={onCreatePatient}>
                {isLoading ? (
                  <div
                    id="results-loading-icon"
                    className="saving-white-ico icon-64"
                    style={{ width: 15, height: 15, marginRight: 3 }}
                  />
                ) : (
                  <i className="ti ti-plus" />
                )}
                Add Patient
              </button>
              <a href="#">
                <button className="btn btn-cancel">Cancel</button>
              </a>
            </div>
          </div>
        </div>
      </div>
      {/* Add patient Modal */}

      {/* Merge patients Modal */}
      <div className="modal override-row create-interrogation-modal popup" id="mergePatients">
        <div className="modalContent">
          <div className="grpHdr">
            <h3>Merge Patients</h3>
          </div>
          <h3>This action will merge the information of patients selected. Do you want to proceed?</h3>
          <div className="row rowStretch">
            <div className="col col-s-100 col20">
              <ul style={{ listStyle: "none", padding: 0 }}>
                <li>Name:</li>
                <li>Date of Birth:</li>
                <li>Gender:</li>
                <li>Status:</li>
              </ul>
            </div>
            <div className="col col-s-100 col20">
              <ul style={{ listStyle: "none", padding: 0 }}>
                <li style={{ textDecoration: "line-through" }}>{mergePatients?.from?.name}</li>
                <li style={{ textDecoration: "line-through" }}>
                  {new Date(mergePatients?.from?.dob).toLocaleDateString()}
                </li>
                <li style={{ textDecoration: "line-through" }}>{mergePatients?.from?.gender}</li>
                <li style={{ textDecoration: "line-through" }}>{mergePatients?.from?.status}</li>
              </ul>
            </div>
            <div className="col col-s-100 col20">
              <ul style={{ listStyle: "none", padding: 0 }}>
                <li>Name:</li>
                <li>Date of Birth:</li>
                <li>Gender:</li>
                <li>Status:</li>
              </ul>
            </div>
            <div className="col col-s-100 col20">
              <ul style={{ listStyle: "none", padding: 0 }}>
                <li>{mergePatients?.to?.name}</li>
                <li>{new Date(mergePatients?.to?.dob).toLocaleDateString()}</li>
                <li>{mergePatients?.to?.gender}</li>
                <li>{mergePatients?.to?.status}</li>
              </ul>
            </div>
          </div>
          <div className="row rowStretch noMargin">
            <div className="col create-ir-col" style={{ display: "flex" }}>
              <button
                id="merge-patients-btn-on-popup"
                className="btn btnSuccess"
                disabled={isLoading}
                onClick={merge}
              >
                {isLoading ? (
                  <div
                    id="results-loading-icon-2"
                    className="saving-white-ico icon-64"
                    style={{ width: 15, height: 15, marginRight: 3 }}
                  />
                ) : (
                  <i className="ti ti-plus" />
                )}
                Merge Patients
              </button>
              <a href="#">
                <button className="btn btn-cancel">Cancel</button>
              </a>
            </div>
          </div>
        </div>
      </div>
      {/* Merge patients Modal */}

      {/* Added serve as backdrop start*/}
      <a href="#" className="close-popup" id="close-popup" />
      {/* Added serve as backdrop end*/}
      {/* .modal */}
    </div>
  );
};

export default PatientsList;
